import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
    ArrowDropDown,
    ArrowDropUp,
    AttachMoney,
    CheckCircle,
    Event,
    GpsFixed,
    People,
    PlayArrow,
    Speed,
    TrendingDown,
    TrendingUp,
    Tv,
    Visibility,
    Warning,
} from '@mui/icons-material';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { campaignIcon } from 'c-main/icons';
import { useCommonTranslation } from 'c-translation';
import type { DashboardStatIcon, DashboardStatSize, DashboardStatStyle } from './dashboardConfig';

// What a single-value widget shows: its figure as a number or a ring, any further figures under
// it, and its change on last year.

const ICONS: Record<Exclude<DashboardStatIcon, 'none'>, React.ReactNode> = {
    campaign: campaignIcon,
    target: <GpsFixed />,
    visibility: <Visibility />,
    trendingUp: <TrendingUp />,
    trendingDown: <TrendingDown />,
    speed: <Speed />,
    play: <PlayArrow />,
    screen: <Tv />,
    people: <People />,
    money: <AttachMoney />,
    calendar: <Event />,
    check: <CheckCircle />,
    warning: <Warning />,
};

// The icon on its tinted tile, beside the widget's heading.
export const DashboardStatIconTile: React.FC<{
    icon: DashboardStatIcon | undefined;
    color: string;
    size?: number;
}> = ({ icon, color, size = 36 }) =>
    icon && icon !== 'none' ? (
        <Box
            data-testid="dashboard-stat-icon"
            sx={{
                width: size,
                height: size,
                borderRadius: 2,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                bgcolor: 'action.hover',
                '& svg': { fontSize: size * 0.6 },
            }}
        >
            {ICONS[icon]}
        </Box>
    ) : null;

const SIZE_VARIANTS: Record<DashboardStatSize, 'h4' | 'h3' | 'h2'> = {
    small: 'h4',
    medium: 'h3',
    large: 'h2',
};

type Props = {
    value: number;
    format: (value: number) => string;
    // The figure's name, under it; left out when the heading already says it.
    label?: string;
    // Further figures, listed under the first.
    secondary: { value: number; text: string; label: string }[];
    style?: DashboardStatStyle;
    size?: DashboardStatSize;
    signColors?: boolean;
    // Whole-percent change on the period compared with, and what that was ("vs 2025");
    // null for none.
    change: number | null;
    changeLabel: string;
    color: string;
    // Sit the figure on the left, under an icon'd heading, rather than in the middle.
    alignStart?: boolean;
    // Height of the space the ring has to fit in.
    height: number;
};

const DashboardStatBody: React.FC<Props> = ({
    value,
    format,
    label,
    secondary,
    style = 'number',
    size = 'large',
    signColors = false,
    change,
    changeLabel,
    color,
    alignStart = false,
    height,
}) => {
    const theme = useTheme();
    const t = useCommonTranslation();
    const ns = 'Pages.DashboardHome.custom.stat';
    const positive = theme.palette.success.main;
    const negative = theme.palette.error.main;
    const ring = style === 'ring';
    // A ring is a ratio read against 100%; a number against zero.
    const good = ring ? value >= 1 : value >= 0;
    const figureColor =
        signColors && !ring && Number.isFinite(value) ? (good ? positive : negative) : undefined;

    const changeLine = change != null && (
        <Stack
            direction="row"
            alignItems="center"
            sx={{ color: change >= 0 ? positive : negative, ml: '-2px' }}
        >
            {change >= 0 ? <ArrowDropUp /> : <ArrowDropDown />}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {`${change >= 0 ? '+' : ''}${change}% ${changeLabel}`}
            </Typography>
        </Stack>
    );
    const secondaryLines = secondary.map(s => (
        <Typography
            key={s.label}
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}
        >
            {`${signColors && s.value > 0 ? '+' : ''}${s.text} ${s.label}`}
        </Typography>
    ));

    if (ring) {
        const percent = Number.isFinite(value) ? value * 100 : 0;
        const diameter = Math.max(60, Math.min(height, 140));
        return (
            <Stack direction="row" spacing={2} alignItems="center" height="100%">
                <Box
                    data-testid="dashboard-stat-ring"
                    sx={{ position: 'relative', width: diameter, height: diameter, flexShrink: 0 }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart
                            innerRadius="72%"
                            outerRadius="100%"
                            data={[
                                {
                                    base: Math.min(percent, 100),
                                    excess: Math.max(0, percent - 100),
                                },
                            ]}
                            startAngle={90}
                            endAngle={-270}
                        >
                            <PolarAngleAxis
                                type="number"
                                domain={[0, Math.max(100, percent)]}
                                angleAxisId={0}
                                tick={false}
                            />
                            <RadialBar
                                background
                                dataKey="base"
                                stackId="ring"
                                cornerRadius={8}
                                fill={color}
                            />
                            <RadialBar
                                dataKey="excess"
                                stackId="ring"
                                cornerRadius={8}
                                fill={theme.palette.secondary.main}
                            />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {Number.isFinite(value) ? `${Math.round(percent)}%` : '—'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    {Number.isFinite(value) && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                ml: '-2px',
                                color: signColors
                                    ? good
                                        ? positive
                                        : negative
                                    : theme.palette.text.secondary,
                            }}
                        >
                            {good ? (
                                <ArrowDropUp fontSize="small" />
                            ) : (
                                <ArrowDropDown fontSize="small" />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {t(`${ns}.${good ? 'aboveTarget' : 'belowTarget'}`)}
                            </Typography>
                        </Stack>
                    )}
                    {secondaryLines}
                    {label && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: 'anywhere' }}
                        >
                            {label}
                        </Typography>
                    )}
                    {changeLine}
                </Box>
            </Stack>
        );
    }

    return (
        <Stack
            alignItems={alignStart ? 'flex-start' : 'center'}
            justifyContent="center"
            height="100%"
            sx={{ minWidth: 0 }}
        >
            <Typography
                variant={SIZE_VARIANTS[size]}
                sx={{ fontWeight: 700, lineHeight: 1.1, color: figureColor }}
                noWrap
            >
                {format(value)}
            </Typography>
            {label && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                >
                    {label}
                </Typography>
            )}
            {secondaryLines}
            {changeLine}
        </Stack>
    );
};

export default DashboardStatBody;
